import React from 'react';
import { motion } from 'framer-motion';


interface SimpleProgressTrackerProps {
  currentQuestion: number;
  totalQuestions: number;
  answers: Array<{
    questionId: string;
    selectedAnswer: string | number | null;
    isCorrect: boolean;
  }>;
  questions: Array<{ id: string; [key: string]: any }>;
  onGoToQuestion?: (questionIndex: number) => void;
}

const SimpleProgressTracker: React.FC<SimpleProgressTrackerProps> = ({
  currentQuestion,
  totalQuestions,
  answers,
  questions,
  onGoToQuestion,
}) => {
  // Calculate progress based on answered questions, not current question number
  const answeredCount = answers.filter(answer => 
    answer.selectedAnswer !== null && answer.selectedAnswer !== ''
  ).length;
  const progress = (answeredCount / totalQuestions) * 100;

  return (
    <div className="bg-white/90 backdrop-blur-md rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-xl border border-white/20">
      {/* Main Progress Bar */}
      <div className="mb-4 sm:mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-600">
            Progress
          </span>
          <span className="text-sm font-bold text-purple-600">
            {answeredCount} / {totalQuestions}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
          <motion.div
            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 sm:h-3 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Question Status Indicators */}
      <div>
        <div className="text-sm font-medium text-gray-600 mb-3">
          Question Status
        </div>
        <div className="grid grid-cols-8 sm:grid-cols-10 gap-2">
          {Array.from({ length: totalQuestions }, (_, index) => {
            const questionNumber = index + 1;
            const isCurrent = questionNumber === currentQuestion;

            // Get the actual question at this index
            const question = questions[index];
            if (!question) return null;

            // Find the answer for this specific question using exact ID match
            const answer = answers.find(a => a.questionId === question.id);

            const isAnswered = !!answer && answer.selectedAnswer !== null && answer.selectedAnswer !== '';
            const hasBeenVisited = !!answer; // Any answer entry means the question was visited
            const canNavigate = hasBeenVisited || isCurrent;

            const getStatusColor = () => {
              if (isCurrent)
                return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-110';
              if (isAnswered)
                return 'bg-gradient-to-r from-blue-400 to-cyan-400 text-white';
              if (hasBeenVisited)
                return 'bg-gray-300 text-gray-600 hover:bg-gray-400'; // Visited but not answered (skipped)
              return 'bg-gray-200 text-gray-500'; // Not visited
            };

            const getStatusIcon = () => {
              return <span className="text-xs">{questionNumber}</span>;
            };

            return (
              <motion.button
                key={index}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.02 }}
                whileHover={{ scale: isAnswered || isCurrent ? 1.1 : 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (onGoToQuestion && canNavigate) {
                    onGoToQuestion(index);
                  }
                }}
                disabled={!canNavigate}
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${getStatusColor()} ${
                  canNavigate && onGoToQuestion
                    ? 'cursor-pointer'
                    : 'cursor-default'
                }`}
                title={
                  isCurrent
                    ? 'Current Question'
                    : isAnswered
                      ? 'Answered - Click to revisit'
                      : hasBeenVisited
                        ? 'Visited (skipped) - Click to revisit'
                        : 'Not visited yet'
                }
              >
                {getStatusIcon()}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SimpleProgressTracker;